import { useState } from 'react';

/**
 * Custom hook for form validation with field-level error tracking
 * @param {Object} schema - Yup validation schema
 * @returns {Object} - Validation state and methods
 */
export const useFormValidation = (schema) => {
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  /**
   * Validate a single field
   * @param {string} fieldName - Name of the field to validate
   * @param {any} value - Value to validate
   * @param {Object} formData - Complete form data for context
   */
  const validateField = async (fieldName, value, formData = {}) => {
    try {
      // Create a partial schema for just this field
      await schema.validateAt(fieldName, { ...formData, [fieldName]: value });

      // Clear error for this field if validation passes
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });

      return true;
    } catch (error) {
      // Set error for this field
      setErrors(prev => ({
        ...prev,
        [fieldName]: error.message
      }));

      return false;
    }
  };

  /**
   * Validate entire form
   * @param {Object} formData - Complete form data to validate
   * @returns {Object} - { isValid: boolean, errors: Object }
   */
  const validateForm = async (formData) => {
    try {
      await schema.validate(formData, { abortEarly: false });
      setErrors({});
      return { isValid: true, errors: {} };
    } catch (error) {
      if (error.name === 'ValidationError') {
        const validationErrors = {};
        error.inner.forEach(err => {
          if (err.path) {
            validationErrors[err.path] = err.message;
          }
        });
        setErrors(validationErrors);
        return { isValid: false, errors: validationErrors };
      }
      return { isValid: false, errors: {} };
    }
  };

  /**
   * Mark a field as touched
   * @param {string} fieldName - Name of the field
   */
  const touchField = (fieldName) => {
    setTouched(prev => ({
      ...prev,
      [fieldName]: true
    }));
  };

  /**
   * Clear all errors
   */
  const clearErrors = () => {
    setErrors({});
    setTouched({});
  };

  /**
   * Clear error for a specific field
   * @param {string} fieldName - Name of the field
   */
  const clearFieldError = (fieldName) => {
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[fieldName];
      return newErrors;
    });
  };

  /**
   * Get error for a specific field (only if touched)
   * @param {string} fieldName - Name of the field
   * @returns {string|null} - Error message or null
   */
  const getFieldError = (fieldName) => {
    return touched[fieldName] ? errors[fieldName] : null;
  };

  /**
   * Check if a field has an error
   * @param {string} fieldName - Name of the field
   * @returns {boolean}
   */
  const hasFieldError = (fieldName) => {
    return touched[fieldName] && !!errors[fieldName];
  };

  return {
    errors,
    touched,
    validateField,
    validateForm,
    touchField,
    clearErrors,
    clearFieldError,
    getFieldError,
    hasFieldError,
  };
};
